import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Bell, BellOff, Check, CheckCheck, Trash2, Search, 
  Filter, RefreshCw, AlertTriangle, AlertCircle, Info, 
  FileText, Database, Mail, Settings, Clock, ChevronRight,
  Loader2, X
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const TIPOS_ALERTA = {
  RECLAMO_VENCIDO: { label: 'Reclamo Vencido', icon: AlertTriangle, color: 'bg-amber-100 text-amber-800 border-amber-200' },
  ERROR_BD: { label: 'Error Base de Datos', icon: Database, color: 'bg-red-100 text-red-800 border-red-200' },
  ERROR_CORREO: { label: 'Error de Correo', icon: Mail, color: 'bg-orange-100 text-orange-800 border-orange-200' },
  SISTEMA: { label: 'Sistema', icon: Settings, color: 'bg-blue-100 text-blue-800 border-blue-200' },
  NUEVO_RECLAMO: { label: 'Nuevo Reclamo', icon: FileText, color: 'bg-green-100 text-green-800 border-green-200' },
  INFO: { label: 'Información', icon: Info, color: 'bg-slate-100 text-slate-800 border-slate-200' },
};

const NotificacionesPage = () => {
  const { api, empresa } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    busqueda: '',
    tipo: '',
    estado: '', // leida, no_leida, todas
  });

  // Colores del tema
  const themeColors = empresa?.colores_tema || {
    primario: '#C9A85C',
    error: '#DC3545',
  };

  const fetchNotificaciones = useCallback(async () => {
    try {
      const response = await api.get('/alertas');
      setNotificaciones(response.data);
    } catch (err) {
      console.error('Error fetching notificaciones:', err);
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchNotificaciones();
  }, [fetchNotificaciones]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchNotificaciones();
    setRefreshing(false);
    toast.success('Notificaciones actualizadas');
  };

  const marcarComoLeida = async (id) => {
    try {
      await api.put(`/alertas/${id}/marcar-leida`);
      setNotificaciones(prev => 
        prev.map(n => n.id === id ? { ...n, leida: true } : n)
      );
      window.dispatchEvent(new Event('alertas-updated'));
      toast.success('Notificación marcada como leída');
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al marcar notificación');
    }
  };

  const marcarTodasComoLeidas = async () => {
    try {
      await api.put('/alertas/marcar-leidas');
      setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
      setSelectedIds([]);
      window.dispatchEvent(new Event('alertas-updated'));
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al marcar notificaciones');
    }
  };

  const marcarSeleccionadasComoLeidas = async () => {
    try {
      await Promise.all(selectedIds.map(id => api.put(`/alertas/${id}/marcar-leida`)));
      setNotificaciones(prev => 
        prev.map(n => selectedIds.includes(n.id) ? { ...n, leida: true } : n)
      );
      setSelectedIds([]);
      window.dispatchEvent(new Event('alertas-updated'));
      toast.success(`${selectedIds.length} notificaciones marcadas como leídas`);
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al marcar notificaciones');
    }
  };

  const eliminarNotificacion = async (id) => {
    try {
      await api.delete(`/alertas/${id}`);
      setNotificaciones(prev => prev.filter(n => n.id !== id));
      setSelectedIds(prev => prev.filter(i => i !== id));
      toast.success('Notificación eliminada');
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al eliminar notificación');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredNotificaciones.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredNotificaciones.map(n => n.id));
    }
  };

  const navigateToReclamo = (reclamoId) => {
    if (reclamoId) {
      navigate(`/admin/reclamos?reclamo=${reclamoId}`);
    }
  };

  // Filtrar notificaciones
  const filteredNotificaciones = notificaciones.filter(n => {
    if (filters.busqueda) {
      const search = filters.busqueda.toLowerCase();
      if (!n.titulo?.toLowerCase().includes(search) && 
          !n.mensaje?.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (filters.tipo && filters.tipo !== '__all__' && n.tipo !== filters.tipo) {
      return false;
    }
    if (filters.estado === 'leida' && !n.leida) return false;
    if (filters.estado === 'no_leida' && n.leida) return false;
    return true;
  });

  const noLeidasCount = notificaciones.filter(n => !n.leida).length;
  const selectedNoLeidas = selectedIds.filter(id => {
    const n = notificaciones.find(n => n.id === id);
    return n && !n.leida;
  });

  const getIconForType = (tipo) => {
    const config = TIPOS_ALERTA[tipo] || TIPOS_ALERTA.INFO;
    const IconComponent = config.icon;
    return <IconComponent className="w-5 h-5" />;
  };

  const getBadgeForType = (tipo) => {
    const config = TIPOS_ALERTA[tipo] || TIPOS_ALERTA.INFO;
    return (
      <Badge variant="outline" className={`${config.color} text-xs`}>
        {config.label}
      </Badge>
    );
  };

  // Convierte folios en el texto a links clickables
  const FOLIO_REGEX = /([A-Z]{2,10}WEB-\d{5,10})/g;
  const renderTextWithLinks = (text, notif) => {
    if (!text) return text;
    const parts = text.split(FOLIO_REGEX);
    if (parts.length === 1) return text;
    const isResolucion = notif.tipo === 'RESOLUCION' || (notif.titulo || '').toLowerCase().includes('resolución');
    const getDest = () => {
      if (notif.reclamo_id) {
        return isResolucion
          ? `/admin/reclamos/${notif.reclamo_id}/resolucion`
          : `/admin/reclamos?detail=${notif.reclamo_id}`;
      }
      // Sin reclamo_id: buscar por folio en la página de reclamos
      return null;
    };
    const dest = getDest();
    return parts.map((part, i) => {
      if (FOLIO_REGEX.test(part)) {
        const linkTo = dest || `/admin/reclamos?busqueda=${part}`;
        return (
          <Link
            key={i}
            to={linkTo}
            className="font-semibold text-blue-600 hover:text-blue-800 hover:underline"
            onClick={(e) => e.stopPropagation()}
            data-testid={`notif-folio-link-${notif.id}`}
          >
            {part}
          </Link>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="notificaciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Bell className="w-7 h-7" style={{ color: themeColors.primario }} />
            Notificaciones
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {notificaciones.length} notificaciones • {noLeidasCount} sin leer
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          {noLeidasCount > 0 && (
            <Button 
              size="sm"
              onClick={marcarTodasComoLeidas}
              style={{ backgroundColor: themeColors.primario }}
              className="text-white"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Marcar todas como leídas
            </Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Búsqueda */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar en notificaciones..."
                className="pl-10"
                value={filters.busqueda}
                onChange={(e) => setFilters({ ...filters, busqueda: e.target.value })}
                data-testid="search-input"
              />
            </div>

            {/* Tipo */}
            <Select
              value={filters.tipo}
              onValueChange={(value) => setFilters({ ...filters, tipo: value })}
            >
              <SelectTrigger className="w-[180px]" data-testid="tipo-filter">
                <SelectValue placeholder="Tipo de alerta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los tipos</SelectItem>
                {Object.entries(TIPOS_ALERTA).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Estado */}
            <Select
              value={filters.estado}
              onValueChange={(value) => setFilters({ ...filters, estado: value })}
            >
              <SelectTrigger className="w-[150px]" data-testid="estado-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                <SelectItem value="no_leida">No leídas</SelectItem>
                <SelectItem value="leida">Leídas</SelectItem>
              </SelectContent>
            </Select>

            {/* Limpiar */}
            {(filters.busqueda || filters.tipo || filters.estado) && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setFilters({ busqueda: '', tipo: '', estado: '' })}
              >
                <X className="w-4 h-4 mr-1" />
                Limpiar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Acciones en lote */}
      {selectedIds.length > 0 && (
        <Card className="border-2 border-dashed" style={{ borderColor: themeColors.primario }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                {selectedIds.length} notificación(es) seleccionada(s)
              </p>
              <div className="flex items-center gap-2">
                {selectedNoLeidas.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={marcarSeleccionadasComoLeidas}
                    data-testid="mark-selected-read-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Marcar como leídas ({selectedNoLeidas.length})
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedIds([])}
                >
                  Cancelar selección
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de notificaciones */}
      <Card className="border-slate-200">
        <CardHeader className="border-b py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={selectedIds.length === filteredNotificaciones.length && filteredNotificaciones.length > 0}
                onCheckedChange={toggleSelectAll}
                data-testid="select-all-checkbox"
              />
              <span className="text-sm text-slate-500">
                {filteredNotificaciones.length} notificación(es)
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredNotificaciones.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <BellOff className="w-12 h-12 mb-4 text-slate-300" />
              <p className="font-medium">No hay notificaciones</p>
              <p className="text-sm">Las notificaciones aparecerán aquí cuando haya actividad</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredNotificaciones.map((notif) => {
                const tipoConfig = TIPOS_ALERTA[notif.tipo] || TIPOS_ALERTA.INFO;
                
                return (
                  <div
                    key={notif.id}
                    className={`flex items-start gap-4 p-4 transition-colors hover:bg-slate-50 ${
                      !notif.leida ? 'bg-blue-50/30' : ''
                    }`}
                    data-testid={`notif-item-${notif.id}`}
                  >
                    {/* Checkbox */}
                    <Checkbox
                      checked={selectedIds.includes(notif.id)}
                      onCheckedChange={() => toggleSelect(notif.id)}
                      className="mt-1"
                      data-testid={`checkbox-${notif.id}`}
                    />

                    {/* Icono */}
                    <div 
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        !notif.leida ? 'ring-2 ring-offset-2' : ''
                      }`}
                      style={{ 
                        backgroundColor: `${tipoConfig.color.includes('amber') ? '#FEF3C7' : 
                          tipoConfig.color.includes('red') ? '#FEE2E2' : 
                          tipoConfig.color.includes('orange') ? '#FFEDD5' : 
                          tipoConfig.color.includes('blue') ? '#DBEAFE' : 
                          tipoConfig.color.includes('green') ? '#DCFCE7' : '#F1F5F9'}`,
                        ringColor: !notif.leida ? themeColors.primario : 'transparent'
                      }}
                    >
                      {getIconForType(notif.tipo)}
                    </div>

                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-medium ${!notif.leida ? 'text-slate-900' : 'text-slate-600'}`}>
                              {renderTextWithLinks(notif.titulo, notif)}
                            </h3>
                            {!notif.leida && (
                              <Badge 
                                className="text-xs px-1.5 py-0 text-white"
                                style={{ backgroundColor: themeColors.primario }}
                              >
                                Nueva
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                            {renderTextWithLinks(notif.mensaje, notif)}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            {getBadgeForType(notif.tipo)}
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: es })}
                            </span>
                            <span className="text-xs text-slate-400">
                              {format(new Date(notif.created_at), 'dd/MM/yyyy HH:mm')}
                            </span>
                          </div>
                        </div>

                        {/* Acciones */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!notif.leida && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => marcarComoLeida(notif.id)}
                              title="Marcar como leída"
                              data-testid={`mark-read-${notif.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {notif.reclamo_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => navigateToReclamo(notif.reclamo_id)}
                              title="Ver reclamo"
                              data-testid={`view-reclamo-${notif.id}`}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-500"
                            onClick={() => eliminarNotificacion(notif.id)}
                            title="Eliminar"
                            data-testid={`delete-${notif.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{notificaciones.length}</p>
            <p className="text-sm text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold" style={{ color: themeColors.primario }}>{noLeidasCount}</p>
            <p className="text-sm text-slate-500">Sin leer</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{notificaciones.length - noLeidasCount}</p>
            <p className="text-sm text-slate-500">Leídas</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">
              {notificaciones.filter(n => n.tipo === 'RECLAMO_VENCIDO').length}
            </p>
            <p className="text-sm text-slate-500">Vencidos</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificacionesPage;
